
import { useState, useEffect, useRef, useCallback } from 'react'
import { useRecaptcha } from '@helpers/useRecaptcha'
import Button from '@ui/Button'
import { SITE } from '@/constants'
import { MAIL_API_URL, buildEmailBody, readEnquiryStatus } from '@/helpers/sendMail'
const INIT = { name: '', email: '', phone: '', message: '' }

function validate(form) {
  const errors = {}
  if (!form.name.trim())    errors.name    = 'Name is required'
  if (!form.email.trim())   errors.email   = 'Email is required'
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = 'Invalid email address'
  if (!form.message.trim()) errors.message = 'Message is required'
  return errors
}

/* Hardcoded test values — replace with real values once API is confirmed */
const MAIL_CC      = ''
const MAIL_SUBJECT = 'Website Enquiry | Sales'


export default function SalesEnquiryForm() {
  const [form,   setForm]   = useState(INIT)
  const [errors, setErrors] = useState({})
  const [status, setStatus] = useState('idle')
 const [captchaToken, setCaptchaToken]   = useState('')
const [pendingSubmit, setPendingSubmit] = useState(false)
const formRef = useRef(null)
const { execute: executeRecaptcha } = useRecaptcha()

  /* Check ?res= on page load after server redirects back */
  useEffect(() => {
    const result = readEnquiryStatus()
    if (result) setStatus(result)
  }, [])

  const handleChange = e => {
    const { name, value } = e.target
    setForm(f => ({ ...f, [name]: value }))
    if (errors[name]) setErrors(er => ({ ...er, [name]: '' }))
  }

  // const handleSubmit = e => {
  //       const errs = validate(form)
  //   if (Object.keys(errs).length) {
  //     e.preventDefault()
  //     setErrors(errs)
  //     return
  //   }
  //   setStatus('loading')
  // }
  
// v3 runs invisibly with no widget/challenge — execute() resolves directly
// with a token. Submission still waits for it before the native POST fires.
const handleSubmit = async e => {
  e.preventDefault()

  const errs = validate(form)
  if (Object.keys(errs).length) {
    setErrors(errs)
    return
  }

  setStatus('loading')
  try {
    const token = await executeRecaptcha('sales_enquiry')
    setCaptchaToken(token)
    setPendingSubmit(true)
  } catch {
    setStatus('idle')
    setErrors(er => ({ ...er, captcha: 'Verification failed. Please try again.' }))
  }
}

// Native form.submit() so the browser performs a full POST navigation
// (fetch can't be used: no CORS) — fires only once the token is in state.
useEffect(() => {
  if (pendingSubmit && captchaToken) {
    setPendingSubmit(false)
    formRef.current?.submit()
  }
}, [pendingSubmit, captchaToken])

  return (
    <section id="contact-cta" className="bg-linear-gradient-light" aria-labelledby="contact-heading">
      <div className="container section-py">
        <div className="contact-grid">

          {/* Left: info */}
          <div data-aos="fade-right">
            <h2 id="contact-heading">Connect with us</h2>
            <p>
              Whether it's a requirement for your existing software or an application,
              whether you are an individual, startup, enterprise or a foreign entity,
              do not hesitate. Our sales team is equipped to answer all your queries.
              All that we need is your trust in us and details to connect with you.
            </p>
            <div className="contact-trust">
              {[
                'Looking for a better Quote?',
                'Stuck with a half developed project?',
                'Want to modernize your application?',
                'Looking at automating your internal processes?',
                'Got a new venture idea?',
                'Looking to restart project with us?',
              ].map(t => (
                <span key={t} className="contact-trust-badge">
                  <i className="fa-solid fa-check" aria-hidden="true" /> {t}
                </span>
              ))}
            </div>
          </div>

          {/* Right: form */}
          <div className="contact-form-wrap" data-aos="fade-left">
            {/*
              Native POST form — server at MAIL_API_URL processes and
              redirects back to /sales-enquiry?res=1 (success) or ?res=2 (error).
              fetch()/AJAX cannot be used — API has no CORS headers.
            */}
            <form
  ref={formRef}
  onSubmit={handleSubmit}
  action={MAIL_API_URL}
              method="POST"
              className="contact-form"
              noValidate
              aria-label="Contact form"
            >
              {/* ── Hidden fields — server-expected field names ── */}
{/* Hidden fields — exact names server reads via Request["..."] */}
<input type="hidden" name="EmailTo"   value={form.email} />
<input type="hidden" name="EmailBcc"  value={SITE.mail_bcc} />
<input type="hidden" name="Subject"   value={MAIL_SUBJECT} />
<input type="hidden" name="EmailBody" value={buildEmailBody(form)} />
<input type="hidden" name="name"      value={form.name} />
<input type="hidden" name="email"     value={form.email} />
<input type="hidden" name="contact"   value={form.phone} />
<input type="hidden" name="EmailAlias"   value={SITE.emailAlias} />
<input type="hidden" name="eMailSenderIDFrom"   value={SITE.eMailSenderIDFrom} />
{/* <input type="hidden" name="ReturnUrl" value={ROUTES.SALES_ENQUIRY} /> */}
{status === 'success' && (
                <p role="status" aria-live="polite" className="form-status form-status-success">
                  Thank you! Your enquiry has been sent — we'll get back to you soon.
                </p>
              )}
              {status === 'error' && (
                <p role="alert" aria-live="assertive" className="form-status form-status-error">
                  Sorry, we couldn't send your enquiry. Please try again or call us directly.
                </p>
              )}
              <p>
                We'd love to talk. Fill in some details and we'll be in touch.
              </p>

              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="name" className="form-label">
                    Full Name <span className="form-req" aria-hidden="true">*</span>
                  </label>
                  <input
                     id="name" name="name" type="text" placeholder="Your name"
                    value={form.name} onChange={handleChange}
                    className={`form-input${errors.name ? ' form-input-error' : ''}`}
                    aria-required="true" aria-invalid={!!errors.name}
                    aria-describedby={errors.name ? 'err-name' : undefined}
                    autoComplete="name"
                  />
                  {errors.name && <span id="err-name" role="alert" className="form-err-msg">{errors.name}</span>}
                </div>

                <div className="form-field">
                  <label htmlFor="email" className="form-label">
                    Email <span className="form-req" aria-hidden="true">*</span>
                  </label>
                  <input
                      id="email" name="email" type="email" placeholder="you@company.com"
                    value={form.email} onChange={handleChange}
                    className={`form-input${errors.email ? ' form-input-error' : ''}`}
                    aria-required="true" aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? 'err-email' : undefined}
                    autoComplete="email"
                  />
                  {errors.email && <span id="err-email" role="alert" className="form-err-msg">{errors.email}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="phone" className="form-label">Phone</label>
                  <input
                    id="phone" name="phone" type="tel" placeholder="+91 XXXXX XXXXX"
                    value={form.phone} onChange={handleChange}
                    className="form-input"
                    autoComplete="tel"
                  />
                </div>
              
                <div className="form-field">
                  <label htmlFor="message" className="form-label">
                    Message <span className="form-req" aria-hidden="true">*</span>
                  </label>
                  <textarea
                    id="message" name="message" rows={2}
                    placeholder="Briefly explain the purpose for this enquiry"
                    value={form.message} onChange={handleChange}
                    className={`form-input form-textarea${errors.message ? ' form-input-error' : ''}`}
                    aria-required="true" aria-invalid={!!errors.message}
                    aria-describedby={errors.message ? 'err-message' : undefined}
                  />
                  {errors.message && <span id="err-message" role="alert" className="form-err-msg">{errors.message}</span>}
                </div>
            
              </div>
                  {/* reCAPTCHA v3 — no visible widget; token travels via hidden field */}
<input type="hidden" name="g-recaptcha-response" value={captchaToken} />
{errors.captcha && (
  <div className="form-field">
    <span role="alert" className="form-err-msg">{errors.captcha}</span>
  </div>
)}
              
              <Button
                type="submit"
                loading={status === 'loading'}
                icon="fa-paper-plane"
                iconPos="right"
                style={{ width: '12rem' }}
              >
                Send Message
              </Button>

              
            </form>
          </div>

        </div>
      </div>
    </section>
  )
}